/* eslint-disable max-lines */
import {
  ALGO_ASSET,
  ALGO_ASSET_ID,
  POOL_TOKEN_UNIT_NAME
} from "@tinymanorg/tinyman-js-sdk";

import {algod} from "../../core/integrations/algosdk/AlgodManager";
import {
  ASSETS_OF_POOLS_REMOVED_FROM_TVL,
  CACHED_ASSETS,
  POOL_CONSTITUENT_ASSET_MIN_TOTAL_SUPPLY,
  USDC_ASSET
} from "./assetConstants";
import webStorage, {STORED_KEYS} from "../../util/storage/webStorage";
import {
  Asset,
  AssetIconsJSON,
  AssetListItemData,
  CurrentAssetPrices,
  SimpleAsset
} from "./assetModels";
import {
  AccountInformationData,
  AppStateAccountData
} from "../../wallet/account/accountTypes";
import {formatNumber, isFiniteNumber} from "../../core/util/number/numberUtils";
import {IS_USING_MAINNET} from "../../core/constants";

/**
 * Check if the Asset is ALGO
 */
function isALGO(assetId: number | undefined | null): boolean {
  return assetId === ALGO_ASSET_ID;
}

/**
 * Returns asset.asset_id
 */
function getAssetId(asset: {id: string | number}): number {
  return Number(asset.id);
}

/**
 * Checks if an asset is a pool token
 */
function isPoolToken(asset: Pick<Asset, "unit_name">) {
  return (
    asset.unit_name === POOL_TOKEN_UNIT_NAME.V1_1 ||
    asset.unit_name === POOL_TOKEN_UNIT_NAME.V2
  );
}

export interface GetAssetInformationByIdOptions {
  alwaysFetch?: boolean;
}

/**
 * Fetches asset data and caches it in a Map.
 * @param {number} id - id of the asset
 * @param {boolean} options.alwaysFetch - Determines whether to always fetch the information of the asset or read it from the cache
 * @returns a promise that resolves with TinymanAnalyticsApiAsset
 */
async function getAssetInformationById(
  id: number,
  options?: GetAssetInformationByIdOptions
): Promise<{asset: SimpleAsset; isDeleted: boolean}> {
  try {
    if (id === ALGO_ASSET_ID) {
      return {asset: ALGO_ASSET, isDeleted: false};
    }

    const memoizedValue = CACHED_ASSETS[`${id}`];

    if (
      memoizedValue &&
      // invalidate cache for this asset if total_amount is not available in the cached data
      memoizedValue.asset.total_amount != null &&
      !options?.alwaysFetch
    ) {
      return memoizedValue;
    }

    const {asset} = await algod.indexer.lookupAssetByID(id).includeAll(true).do();

    const unitName = asset.params.unitName || "";
    const assetData: SimpleAsset = {
      id: `${asset.index}`,
      decimals: Number(asset.params.decimals),
      is_liquidity_token: isPoolToken({unit_name: unitName}),
      name: asset.params.name || "",
      unit_name: unitName,
      url: "",
      total_amount: String(asset.params.total),
      clawback_address: asset.params.clawback ?? ""
    };

    CACHED_ASSETS[`${id}`] = {
      asset: assetData,
      isDeleted: asset.deleted ?? false
    };
    webStorage.local.setItem(STORED_KEYS.TINYMAN_CACHED_ASSETS, CACHED_ASSETS);

    return {asset: assetData, isDeleted: asset.deleted ?? false};
  } catch (error: any) {
    throw new Error(error?.message || "Failed to fetch asset information");
  }
}

/**
 * Checks if the provided value is a valid ASA ID
 * @param id - Asset ID
 */
function isValidAssetID(id: unknown): id is number {
  const parsedId = id == null ? NaN : Number(id);

  return Number.isFinite(parsedId) && parsedId >= 0;
}

/**
 * Searches the asset within the account and returns its balance
 */
function getAssetBalance(
  account: AccountInformationData | null,
  assetID: number
): bigint {
  return isALGO(assetID)
    ? account?.amount || 0n
    : account?.assets?.find((asset) => asset.assetId === BigInt(assetID))?.amount || 0n;
}

function generateAlgoAssetListItemData(
  account: null | undefined | AppStateAccountData
): AssetListItemData {
  return {
    ...ALGO_ASSET,
    balance: account?.amount || 0n
  };
}

function generateUSDCAssetListItemData(
  account: null | AppStateAccountData
): AssetListItemData {
  return {
    ...USDC_ASSET,
    balance: getAssetBalance(account, getAssetId(USDC_ASSET))
  };
}

/**
 * Checks if account already has the asset
 */
function hasAlreadyOptedIntoAsset(accountAssets: AssetListItemData[], assetId: number) {
  return isALGO(assetId) || accountAssets.some((asset) => getAssetId(asset) === assetId);
}

/**
 * Checks if the asset is a pool token belongs to Tinyman archived v1 contract
 */
function isArchivedV1PoolToken(unitName: string) {
  return unitName === POOL_TOKEN_UNIT_NAME.V1;
}

function canAssetBeUsedAsPoolConstituent(asset: Asset): boolean {
  return !(
    parseFloat(asset.total_amount) < POOL_CONSTITUENT_ASSET_MIN_TOTAL_SUPPLY ||
    isArchivedV1PoolToken(asset.unit_name)
  );
}

function getAssetAmountFormatMaxFractionDigits({
  assetDecimals,
  amountInBaseUnits
}: {
  assetDecimals?: number;
  amountInBaseUnits: number;
}) {
  let maxFractionDigits = 2;

  if (amountInBaseUnits < 1 && assetDecimals) {
    maxFractionDigits = assetDecimals;
  }

  return maxFractionDigits;
}

function formatAssetAmount({
  assetDecimals,
  amount,
  formatter,
  unitName
}: {
  amount: number;
  assetDecimals?: number;
  formatter?: ReturnType<typeof formatNumber>;
  unitName?: string;
}) {
  const numberFormatter =
    formatter ||
    formatNumber({
      notation: "compact",
      maximumFractionDigits: getAssetAmountFormatMaxFractionDigits({
        assetDecimals,
        amountInBaseUnits: amount
      }),
      minimumFractionDigits: 0,
      // eslint-disable-next-line no-magic-numbers
      maximumSignificantDigits: amount >= 1000 ? 5 : undefined
    });
  const formattedAmount = numberFormatter(amount);

  return unitName ? `${formattedAmount} ${unitName}` : formattedAmount;
}

async function getAssetPairDetail(
  asset1ID: null | number,
  asset2ID: null | number
): Promise<{
  asset1Detail: SimpleAsset | undefined;
  asset2Detail: SimpleAsset | undefined;
}> {
  const assetIDsToSearch: number[] = [];
  let asset1Detail;
  let asset2Detail;

  if (isALGO(asset1ID)) {
    asset1Detail = ALGO_ASSET;
  } else if (isValidAssetID(asset1ID)) {
    assetIDsToSearch.push(asset1ID);
  }

  if (isALGO(asset2ID)) {
    asset2Detail = ALGO_ASSET;
  } else if (isValidAssetID(asset2ID)) {
    assetIDsToSearch.push(asset2ID);
  }

  if (assetIDsToSearch.length) {
    const assetDetails = await Promise.all(
      assetIDsToSearch.map((id) => getAssetInformationById(id))
    );

    const assets = assetDetails
      .filter((assetDetail) => !assetDetail.isDeleted)
      .map<SimpleAsset>((assetDetail) => assetDetail.asset);

    if (assets.length) {
      // eslint-disable-next-line no-magic-numbers
      if (assetIDsToSearch.length === 2) {
        asset1Detail = assets[0];
        asset2Detail = assets[1];
      } else if (assetIDsToSearch.length === 1) {
        if (getAssetId(assets[0]) === asset1ID) {
          asset1Detail = assets[0];
        } else {
          asset2Detail = assets[0];
        }
      }
    }
  }

  return {
    asset1Detail,
    asset2Detail
  };
}

function findAssetWithinAccount(
  account: null | undefined | AppStateAccountData,
  accountAssets: AssetListItemData[],
  assetId: number | undefined | null
) {
  return isALGO(assetId)
    ? generateAlgoAssetListItemData(account)
    : accountAssets.find((asset) => getAssetId(asset) === assetId);
}

/**
 * Extends asset object with balance information that is available on AccountData
 */
function mapAssetToAssetListItemData(
  account: null | AppStateAccountData,
  accountAssets: AssetListItemData[]
) {
  return (asset: Asset): AssetListItemData => {
    const assetId = getAssetId(asset);
    let data: AssetListItemData;

    if (isALGO(assetId)) {
      data = {
        ...asset,
        balance: account?.amount || 0n
      };
    } else {
      const accountAsset = findAssetWithinAccount(account, accountAssets, assetId);

      data = {
        ...asset,
        balance: accountAsset ? accountAsset.balance : null
      };
    }

    return data;
  };
}

/**
 * @param priceData - The response of the /current-price-assets endpoint
 * @param assetID - Asset's ID
 * @param amount - The amount we want to convert to USD
 * @returns USD equivalent of the amount if the price data exists, returns null otherwise
 */
function calculateAssetPrice(
  priceData: null | CurrentAssetPrices,
  assetID: string,
  amount: null | number
) {
  const assetPriceData = (priceData && priceData[assetID]) || null;
  const price =
    assetPriceData && typeof amount === "number" && amount * assetPriceData.price_in_usd;

  return isFiniteNumber(price) ? price : null;
}

/**
 * Prefixes asset.unit_name with $ sign and suffixes with `- {asset.asset_id}` if it is not ALGO
 */
function combineAssetUnitAndId(asset: Pick<Asset, "id" | "unit_name">): string {
  const assetId = getAssetId(asset);
  let text = "";

  if (isALGO(assetId)) {
    text = `$${asset.unit_name}`;
  } else if (asset.unit_name) {
    text = `$${asset.unit_name} - ${assetId}`;
  } else {
    text = `#${assetId}`;
  }

  return text;
}

/**
 * Generates URL for the asset's logo
 */
function getAssetLogo(icons: AssetIconsJSON, assetId: number) {
  return IS_USING_MAINNET || assetId === ALGO_ASSET_ID
    ? icons[assetId] || getAssetPlaceholderLogo(icons)
    : getAssetPlaceholderLogo(icons);
}

function getAssetPlaceholderLogo(icons: AssetIconsJSON) {
  return icons["icon-placeholder"];
}

/**
 * Order the list in ascending order with respect to asset id
 */
function orderByAssetId<Data extends {id: string | number}>(list: Data[]) {
  const orderedList = [...list];

  orderedList.sort((item1, item2) => getAssetId(item2) - getAssetId(item1));

  return orderedList;
}

/**
 * Returns `asset.unit_name` if it exists or `#${asset.asset_id}` if it doesn't
 */
function getAssetUnitDisplayName(
  asset:
    | undefined
    | null
    | (Pick<AssetListItemData, "id"> & Partial<Pick<AssetListItemData, "unit_name">>),
  withSymbol = false
): string {
  let name = "";

  if (asset?.unit_name) {
    name = withSymbol ? `$${asset.unit_name}` : asset.unit_name;
  } else if (asset?.id) {
    name = `#${getAssetId(asset)}`;
  }

  return name;
}

function createPairNameFromConstituents(
  assets: [Pick<Asset, "unit_name" | "id">, Pick<Asset, "unit_name" | "id">],
  /* the content that will be inserted between asset names */
  separator = " / "
) {
  const [asset1, asset2] = orderByAssetId([assets[0], assets[1]]);

  return `${getAssetUnitDisplayName(asset1)}${separator}${getAssetUnitDisplayName(
    asset2
  )}`;
}

function isNonLiquidAsset(asset: Pick<Asset, "id">) {
  return ASSETS_OF_POOLS_REMOVED_FROM_TVL.has(Number(asset.id));
}

export {
  isALGO,
  getAssetId,
  isPoolToken,
  getAssetInformationById,
  isValidAssetID,
  generateAlgoAssetListItemData,
  generateUSDCAssetListItemData,
  hasAlreadyOptedIntoAsset,
  getAssetBalance,
  canAssetBeUsedAsPoolConstituent,
  formatAssetAmount,
  getAssetPairDetail,
  mapAssetToAssetListItemData,
  findAssetWithinAccount,
  calculateAssetPrice,
  combineAssetUnitAndId,
  getAssetLogo,
  getAssetPlaceholderLogo,
  orderByAssetId,
  getAssetUnitDisplayName,
  createPairNameFromConstituents,
  isNonLiquidAsset
};
